<template>
  <data-loader>
    <div class="simulation-flashcard-container">
      <div class="simulation-flashcard-header">
        <flashcard-header
          :title-text="`Question #${flashcardIndex < 1 ? 1 : flashcardIndex + 1}`"
        >
          <span
            v-if="isSimulationComplete"
            class="inline-row"
          >
            <span>{{ sectionTally.correct }}/{{ flashcards.length }}</span>
            <button
              class="button-icon"
              tabindex="0"
              title="Return to Section Review"
              @click="goToSectionReview"
              @keydown.enter="goToSectionReview"
            >
              <icon
                name="arrow-in-box"
                class="icon"
              ></icon>
            </button>
          </span>
          <span
            v-else
          >
            {{ formattedTime(timeLeftInSeconds) }}
          </span>
        </flashcard-header>
      </div>
      <div class="simulation-flashcard-sidebar">
        <div class="navigation-buttons-container">
          <navigation-button
            v-for="(flashcardStatus, index) in flashcardListStatuses"
            :key="flashcardStatus.id"
            :flashcard-status="flashcardStatus"
            :is-selected="flashcard.id === flashcardStatus.id"
            :is-simulation-complete="isSimulationComplete"
            @navigate="navigate"
          >
            {{ formatQuestionNumber(index) }}
          </navigation-button>
        </div>
      </div>
      <div class="simulation-flashcard-body">
        <multiple-choice-flashcard
          :flashcard="flashcard"
          :question-html="questionHtml"
          :flashcard-state="flashcardState"
          :selected-answer-id="selectedAnswerId"
          :patient-box-topic-id="patientBoxTopicId"
          :patient-box-html="patientBoxHtml"
          :is-simulation-complete="isSimulationComplete"
          @selectAnswer="selectAnswer"
          @toggleStrikeThrough="toggleStrikeThrough"
        />
      </div>
      <div class="simulation-flashcard-footer">
        <flashcard-footer
          :next-flashcard-id="nextFlashcardId"
          :previous-flashcard-id="previousFlashcardId"
          :bookmark="bookmark"
          :is-simulation-complete="isSimulationComplete"
          @navigate="navigate"
          @toggleBookmark="toggleBookmark"
          @clickHighlight="clickHighlight"
        />
      </div>
    </div>
  </data-loader>
</template>

<script>
import MultipleChoiceFlashcard from '@/components/sims/flashcards/MultipleChoiceFlashcard.vue';
import FlashcardHeader from '@/components/sims/flashcards/FlashcardHeader.vue';
import FlashcardFooter from '@/components/sims/flashcards/FlashcardFooter.vue';
import FlashcardViewMixin from '@/mixins/flashcardViews';
import NavigationButton from '@/components/sims/flashcards/NavigationButton.vue';
import BookmarksMixin from '@/mixins/bookmarks';
import WuxSimulationsMixin from '@/mixins/wux-simulations';
import { highlightSelection, stripHTMLWhiteSpace, isAllHighlighted, removeHighlight, removeRedundantMarks } from '@/utils/dom';
import DataLoader from '@/components/sims/DataLoader.vue';
import Icon from '@components/singletons/icon.vue';
import AccommodationsMixin from '@/mixins/accommodations';

export default {
  components: {
    DataLoader,
    FlashcardHeader,
    FlashcardFooter,
    Icon,
    NavigationButton,
    MultipleChoiceFlashcard,
  },
  mixins: [FlashcardViewMixin, BookmarksMixin, WuxSimulationsMixin, AccommodationsMixin],
  data() {
    return {
      timeLeftInSeconds: null,
      timerInterval: null,
    };
  },
  computed: {
    flashcard() {
      return this.flashcards[this.flashcardIndex] || {};
    },
    flashcardId() {
      return +this.$route.params['flashcardId'];
    },
    flashcardIndex() {
      return this.flashcards.findIndex( f => f.id == this.flashcardId );
    },
    flashcardSetIndex() {
      return this.flashcardSetIds.findIndex( id => id == this.flashcardId );
    },
    flashcardState() {
      return this.getFlashcardState(this.flashcardId, this.sectionId);
    },
    // This is a filtered set of flashcard ids that are used when filter is set to true
    // ie. when we are just reviewing flagged or incomplete flashcards
    flashcardSetIds() {
      return this.$store.getters['flashcardSet/ids'];
    },
    filtered() {
      return this.$route.query?.filter;
    },
    patientBoxTopicId() {
      const groupingId = this.getGroupingId(this.flashcardId);

      return this.groupingAssetAssociationsList.find(aa => aa.assetAId === groupingId && aa.displayType === 'scenario_box')?.assetBId;
    },
    patientBoxTopic() {
      if (this.patientBoxTopicId) {
        return this.$store.getters['topics/all']?.find(topic => topic.id === this.patientBoxTopicId);
      }
      return null;
    },
    patientBoxHtml() {
      if (this.patientBoxTopic?.content) {
        const baseHtml = this.patientBoxTopic.content.replaceAll(/style="[^"]*"/g, '');;
        return this.applyHighlightStates(baseHtml, 'aside-container');
      }
      return '';
    },
    nextFlashcardId() {
      if (this.filtered) {
        return this.flashcardSetIds[this.flashcardSetIndex + 1];
      }

      if (this.flashcardIndex >= this.flashcards.length - 1) return null;
      return this.flashcards[this.flashcardIndex + 1].id;
    },
    previousFlashcardId() {
      if (this.filtered) {
        return this.flashcardSetIds[this.flashcardSetIndex - 1];
      }

      if (this.flashcardIndex <= 0)  return null;
      return this.flashcards[this.flashcardIndex - 1].id;
    },
    highlightStates() {
      if (this.flashcard) {
        return this.$store.getters['highlightStates/get'](this.flashcardId, this.sectionId) || [];
      }
      return [];
    },
    questionHtml() {
      if (this.flashcard?.question) {
        return this.applyHighlightStates(this.flashcard.question, 'flashcard-content');
      }
      return '';
    },
    selectedAnswerId() {
      return (this.answerIds && this.answerIds[0]) || 0;
    },
  },
  watch: {
    flashcardState(newState, oldState) {
      if (newState !== oldState) {
        this.answerIds = newState.answerIds || [];
      }
    },
    timeLeftInSeconds(currentValue) {
      if (currentValue < 0) {
        this.submitSection();
      }
    },
    flashcardId() {
      this.scrollToTop();
      if (this.selectedAnswerId && !this.isAnswerIdValid(this.selectedAnswerId)) {
        this.selectAnswer(0);
      }
    },
  },
  mounted() {
    if (this.flashcardState.answerIds) {
      this.setAnswerIds([...this.flashcardState.answerIds]);
    }
    if (this.filtered && this.flashcardSetIds.length === 0) {
      this.navigate(null);
    }

    if (this.isSimulationComplete) {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }
      return;
    }
    this.timerInterval = setInterval(() => {
      try {
        const timeInMinutes = this.userTime || this.section.timeInMinutes;
        this.timerTick(this.inProgressStatus, timeInMinutes);
      }
      catch(e) {
        clearInterval(this.timerInterval);
      }
    }, 250);
  },
  beforeUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  },
  methods: {
    applyHighlightStates(baseHtml, containerClass) {
      let html = stripHTMLWhiteSpace(baseHtml);
      if (this.highlightStates.length > 0) {
        const container = document.createElement('div');
        container.innerHTML = html;

        this.highlightStates.forEach((highlightState) => {
          if (highlightState.container === containerClass) {
            if (isAllHighlighted(container, {
              startContainerText: highlightState.startContainerText,
              endContainerText: highlightState.endContainerText,
            })) {
              html = removeHighlight(container, {
                startContainerText: highlightState.startContainerText,
                endContainerText: highlightState.endContainerText,
                startOffset: highlightState.startOffset,
                endOffset: highlightState.endOffset,
              });
            } else {
              html = highlightSelection(container, {
                startContainerText: highlightState.startContainerText,
                endContainerText: highlightState.endContainerText,
                startOffset: highlightState.startOffset,
                endOffset: highlightState.endOffset,
              });
            }
            html = removeRedundantMarks(html);
            container.innerHTML = html;
          }
        });
      }
      return html;
    },
    formatQuestionNumber(flashcardId) {
      if (flashcardId >= 9) {
        return `${flashcardId + 1}`;
      }
      return `0${flashcardId + 1}`;
    },
    navigate(flashcardId, buttonType) {
      const answerUpdated = !!this.updateAnswer();

      // We want to keep the query string parameter if it exists and if the flashcardId is
      // contained in the flashcardSetId
      const query = (this.filtered && this.flashcardSetIds.includes(flashcardId)) ? { filter: true } : {};

      if (flashcardId) {
        this.$analytics.events.simulationNavigation({
          simulation: this.simulation,
          section: this.section,
          flashcardOriginId: this.flashcardId,
          flashcardDestinationId: flashcardId,
          buttonType,
          answerUpdated,
        });
        return this.$router.push({
          name: 'simSectionFlashcard#show',
          params: {
            simId: this.simId,
            sectionId: this.sectionId,
            flashcardId: flashcardId,
          },
          query,
        });
      }
      // if flashcardId is falsy, we are at the end of the flashcard list
      return this.$router.push({
        name: 'simSection#review',
        params: {
          simId: this.simId,
          sectionId: this.sectionId,
        },
      });
    },
    isAnswerIdValid(answerId) {
      return this.flashcard?.answers?.some(answer => answer.id === answerId);
    },
    selectAnswer(answerId) {
      if (this.checkTimer() === false) return;
      if(!answerId) {
        this.answerIds = [];
        return;
      }
      if (!this.isAnswerIdValid(answerId)) {
        return;
      }

      this.setAnswerIds([answerId]);
    },
    toggleStrikeThrough(answer, strikeThrough) {
      // handling the analytics event at this level so we can
      // use the values for simulation, section, and flashcard
      this.$analytics.events.simulationStrikeThrough({
        simulation: this.simulation,
        section: this.section,
        flashcard: this.flashcard,
        answer,
        strikeThrough,
      });
    },
    clickHighlight() {
      const selection = document.getSelection();
      if (selection.isCollapsed) {
        console.error('No text selected');
        return;
      };

      const range = selection.getRangeAt(0);
      const commonAncestor = range.commonAncestorContainer;

      const flashcardContent = document.getElementsByClassName('flashcard-content')[0];
      const asideContainer = document.getElementsByClassName('aside-container')[0];

      const insideFlashcardContent = flashcardContent.contains(commonAncestor);
      const insideAsideContainer = asideContainer?.contains(commonAncestor);

      if (!insideFlashcardContent && !insideAsideContainer) {
        console.error('Selected text is not contained within flashcard content or patient box.');
        return;
      };

      const highlightParams = {
        flashcardId: this.flashcardId,
        categoryId: this.sectionId,
        container: insideFlashcardContent ? 'flashcard-content' : 'aside-container',
        startOffset: range.startOffset,
        endOffset: range.endOffset,
        startContainerText: range.startContainer.textContent,
        endContainerText: range.endContainer.textContent,
      };

      this.$store.dispatch('highlightStates/set', highlightParams);
      this.$analytics.events.simulationHighlight({
        simulation: this.simulation,
        section: this.section,
        flashcard: this.flashcard,
        highlightContainer: highlightParams.container,
      });
    },
    checkTimer() {
      this.timerTick(this.inProgressStatus, this.section.timeInMinutes);
      if (this.timeLeftInSeconds < 0) {
        this.submitSection();
        return false;
      }
      return true;
    },
    updateAnswer() {
      if (this.isSimulationComplete) return;
      if (this.checkTimer() === false) return;
      // If it has been answered and the answer didn't change, skip
      if (this.flashcardState.answerIds && this.flashcardState.answerIds[0] === this.selectedAnswerId) {
        return;
      };

      // If it has not been answered and there is no answer selected, skip
      if (
        this.flashcardState.answerIds &&
        !this.flashcardState.answerIds[0] &&
        !this.selectedAnswerId
      ) {
        return;
      }

      // if the answer is not valid, skip
      if (this.selectedAnswerId && !this.isAnswerIdValid(this.selectedAnswerId)) {
        return;
      }

      this.correct = this.evaluateAnswer(this.flashcard, this.answerIds);

      const flashcardStatusId = (this.answerIds.length > 0)
        ? this.$store.getters['flashcardStatuses/greenFlashcardStatus'].id
        : this.$store.getters['flashcardStatuses/unansweredFlashcardStatus'].id;

      const params = {
        correct: this.correct,
        flashcardId: this.flashcard.id,
        flashcardStatusId,
        categoryId: this.sectionId,
      };

      this.createFlashcardState(params);
      this.$analytics.events.updateSimulationAnswer({
        simulation: this.simulation,
        section: this.section,
        flashcard: this.flashcard,
        answerIds: this.answerIds,
      });
      return true;
    },
    scrollToTop() {
      const el = document.querySelector('.simulation-flashcard-body');
      if (el) {
        el.scrollTop = 0;
      }
    },
    goToSectionReview() {
      this.$router.push({
        name: 'simSection#review',
        params: {
          simId: this.simId,
          sectionId: this.sectionId,
        },
      });
    },
  },
};
</script>

<style lang='scss' scoped>
$header-height: 5.5rem;
$sidebar-width: 4.5rem;
$footer-height: 3.5rem;
$right-margin: 3rem;

.simulation-flashcard-container {
  margin: 0;
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  z-index: 1000;
  background: white;

  .simulation-flashcard-header {
    position: fixed;
    top: 0px;
    left: 0px;
    right: 0px;
    height: $header-height;
    margin-right: $right-margin;
    z-index: 1001;
  }

  .simulation-flashcard-sidebar {
    position: fixed;
    top: $header-height;
    left: 0px;
    bottom: 0px;
    width: $sidebar-width;
    padding: 1rem 0;
    background-color: #E9E9E9;
    overflow-y: auto;

    .navigation-buttons-container {
      display: flex;
      align-items: center;
      flex-direction: column;
      gap: 0.5rem;
    }
  }

  .simulation-flashcard-body {
    position: fixed;
    top: $header-height;
    left: $sidebar-width;
    bottom: $footer-height;
    right: 0px;
    margin-left: 3rem;
    margin-right: 0;
    padding-right: 1rem;
    overflow-y: auto;
  }

  .simulation-flashcard-footer {
    z-index: 1001;
    background: white;
    position: fixed;
    height: $footer-height;
    left: $sidebar-width;
    bottom: 0px;
    right: 0px;
  }
}

.button-icon {
  background-color: transparent;
  padding: 0;
  margin: 0;
  line-height: 0;
}

.icon {
  width: 1.5rem;
  height: 1.5rem;
}

.inline-row {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}
</style>
